import * as React from "react";

export interface ExpandingButtonProps {
  icon: string;
  text: string;
  expandDirection?: 'left' | 'right';
  variant?: 'favorite' | 'block' | 'custom';
  active?: boolean;
  onClick: (e: React.MouseEvent) => void;
  iconColor?: string;
  className?: string;
  title?: string;
}

/**
 * ExpandingButton - Generic expanding button component
 *
 * A button that starts as an icon-only (38px width) and expands on hover
 * to reveal text. Used for Favorite, Block, and other action buttons.
 *
 * Features:
 * - Expands left or right on hover
 * - Icon-only compact state (38px)
 * - Text reveals on hover with smooth animation
 * - Supports different variants (favorite, block, custom)
 * - Active/inactive states
 *
 * @example
 * <ExpandingButton
 *   icon="fad fa-heart"
 *   text="Favorite"
 *   variant="favorite"
 *   expandDirection="left"
 *   active={false}
 *   onClick={handleClick}
 * />
 */
export const ExpandingButton: React.FC<ExpandingButtonProps> = ({
  icon,
  text,
  expandDirection = 'right',
  variant = 'custom',
  active = false,
  onClick,
  iconColor,
  className = '',
  title
}) => {
  // Determine base class based on variant
  const baseClass = variant === 'favorite' ? 'favorite-button' :
    variant === 'block' ? 'block-button' :
      'expanding-button';

  // Add expand direction modifier
  const expandClass = expandDirection === 'left' ? `${baseClass}--expand-left` : '';

  // Add active state class
  const activeClass = active ?
    (variant === 'favorite' ? 'favorited' : variant === 'block' ? 'blocked' : 'active') :
    '';

  // Combine all classes
  const classes = `text-decoration-none ${baseClass} ${expandClass} ${activeClass} ${className}`.trim();

  // Determine text class based on variant
  const textClass = variant === 'favorite' ? 'favorite-button__text' :
    variant === 'block' ? 'block-button__text' :
      'expanding-button__text';

  return (
    <button
      type="button"
      onClick={onClick}
      className={classes}
      title={title}
    >
      <i
        className={icon}
        style={iconColor ? { color: iconColor } : undefined}
        aria-hidden="true"
      ></i>
      <span className={textClass}>{text}</span>
    </button>
  );
};

export default ExpandingButton;
